import React, { useState, useMemo, useEffect } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { Link } from 'react-router-dom';
import { 
  TextField, 
  IconButton,
  Avatar, 
  Box, 
  Typography,
  Tooltip,
} from '@mui/material';
import {
  EventNote as RescheduleIcon,
  AddCircle as WalkInIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import appointmentService from '../services/appointmentTimelineService'; // Import the service
import AppointmentTimelineKPI from '../Header/AppointmentTimelineKPI'; // Import KPI component
import ActionTableButton from '../Buttons/ActionTableButton'; // Import action button component
import AppointmentFiltersHeader from '../Header/AppointmentFilterHeader';
import { CallButton } from '../Buttons/CallPatientComponent';
import { RescheduleButton} from '../Buttons/RescheduleComponent';
import PlayButtonComponent from '../Buttons/PlayButtonComponent';
import WalkinButtonComponent from '../Buttons/WalkinButtonComponent'; // Import Walk-in button component
import NoShowComponent from '../Buttons/NoShowComponent';


// Main component to display a table of appointments
const AppointmentTimelineTable = ({ 
  appointments: propAppointments = [], 
  onDeleteAppointments, 
  onStatusChange,
  onCallAppointment,
  onStartConsultation,
  onRescheduleAppointment,
  onWalkInAppointment,
  onRescheduleAppointments,
  onCancelAppointments,
  onReloadAppointments,
  onDateRangeChange,
  onViewChange 
}) => {
  
  // Base states for the component
  const [appointments, setAppointments] = useState([]); // List of processed appointments
  const [searchTerm, setSearchTerm] = useState(''); // General search term
  const [rowSelection, setRowSelection] = useState({}); // Control of selected rows
  const [isLoading, setIsLoading] = useState(true); // Loading indicator
  const [globalFilter, setGlobalFilter] = useState(''); // Global filter for MaterialReactTable
  const [activeFilters, setActiveFilters] = useState({}); // Active filters for highlighting
  
  // State to maintain original appointments (without transformation)
  const [originalAppointments, setOriginalAppointments] = useState([]);
  // State to maintain filtered appointments in original format
  const [filteredOriginalAppointments, setFilteredOriginalAppointments] = useState([]);
  
  // State for cell editing
  const [editingCell, setEditingCell] = useState(null); // {rowId, columnId, value}
  const [editValue, setEditValue] = useState('');
  
  // State for action menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowForAction, setSelectedRowForAction] = useState(null);

  // Timeline view states
  const [currentTimelineView, setCurrentTimelineView] = useState('day');
  const [selectedDateRange, setSelectedDateRange] = useState({
    start: null,
    end: null
  });
  const [timelineViewSettings, setTimelineViewSettings] = useState({
    groupBy: 'hour',
    showHours: true,
    showMinutes: true
  });

  // Fixed transformApiData function for AppointmentTimelineTable
// This function should replace the existing transformApiData function in your table component

const transformApiData = (apiData) => {
  if (!apiData || !Array.isArray(apiData)) return [];
  
  console.log('🔄 Transforming API data:', {
    totalRecords: apiData.length,
    firstRecord: apiData[0] ? {
      _id: apiData[0]._id,
      appointmentId: apiData[0].appointmentId,
      patientName: apiData[0].patientName,
      despachoName: apiData[0].despachoName,
      productoCitaName: apiData[0].productoCitaName
    } : null
  });
  
  const transformedData = apiData.map((appointment, index) => {
    if (!appointment._id) {
      console.error(`❌ Missing _id in appointment ${index}:`, appointment);
    }
    
    const transformed = {
      id: appointment._id,
      _id: appointment._id,
      appointmentId: appointment.appointmentId || '',
      patientName: appointment.patientName || '',
      patientAvatar: appointment.patientAvatar || '',
      dateOfBirth: appointment.dateOfBirth ? new Date(appointment.dateOfBirth).toISOString().split('T')[0] : '',
      address: appointment.address || '',
      userid: appointment.userid || '',
      meetinguserid: appointment.meetinguserid || '',
      doctorName: appointment.doctorName || '',
      doctorRole: appointment.doctorRole || '',
      
      // ✅ PRODUCTOS Y DESPACHO
      productoCita: appointment.productoCita || '',
      productoCitaName: appointment.productoCitaName || '',
      productoCitaCode: appointment.productoCitaCode || '',
      productoCitaPrice: appointment.productoCitaPrice || 0,
      
      despachoMedico: appointment.despachoMedico || '',
      despachoName: appointment.despachoName || '',
      despachoCode: appointment.despachoCode || '',
      
      date: appointment.date ? new Date(appointment.date).toISOString().split('T')[0] : '',
      startTime: appointment.startTime || '',
      endTime: appointment.endTime || '',
      status: appointment.status || 'Pending',
      medicalHistory: appointment.medicalHistory || '',
      appointmentType: appointment.appointmentType || '',
      notes: appointment.notes || '',
      checkInTime: appointment.checkInTime ? new Date(appointment.checkInTime).toISOString() : '',
      waitTime: appointment.waitTime || 0,
      consultationDuration: appointment.consultationDuration || 0,
      isaccepted: appointment.isaccepted || false,
      isWalkIn: appointment.isWalkIn || false,
      rescheduledCount: appointment.rescheduledCount || 0,
      
      // ✅ INFORMACIÓN DE PAGO
      totalAmount: appointment.totalAmount || 0,
      paymentStatus: appointment.paymentStatus || 'Pending',
      paidAmount: appointment.paidAmount || 0,
      
      createdAt: appointment.createdAt ? new Date(appointment.createdAt).toISOString().split('T')[0] : '',
      updatedAt: appointment.updatedAt ? new Date(appointment.updatedAt).toISOString().split('T')[0] : ''
    };
    
    if (index < 3) {
      console.log(`✅ Transformed appointment ${index}:`, {
        originalId: appointment._id,
        transformedId: transformed.id,
        appointmentId: transformed.appointmentId,
        patientName: transformed.patientName,
        despachoName: transformed.despachoName,
        productoCitaName: transformed.productoCitaName,
        status: transformed.status
      });
    }
    
    return transformed;
  });
  
  console.log(`✅ Transformation complete: ${transformedData.length} appointments transformed`);
  return transformedData;
};
  
  // Effect to transform and load appointments from props
  // Agrega este código debug en tu componente AppointmentTimelineTable

// 1. DESPUÉS de la transformación de datos, en el useEffect:
// Effect to transform and load appointments from props
useEffect(() => {
  console.log('📊 useEffect triggered - propAppointments:', {
    length: propAppointments.length,
    hasData: propAppointments.length > 0
  });

  if (propAppointments.length > 0) {
    // Save original appointments
    setOriginalAppointments(propAppointments);
    setFilteredOriginalAppointments(propAppointments);
    
    // Transform API data
    const transformedData = transformApiData(propAppointments);
    
    // 🔍 CRITICAL DEBUG
    console.log('✅ TRANSFORMATION COMPLETE:', {
      originalCount: propAppointments.length,
      transformedCount: transformedData.length,
      firstThreeRecords: transformedData.slice(0, 3).map(apt => ({
        id: apt.id,
        patientName: apt.patientName,
        status: apt.status,
        date: apt.date
      }))
    });
    
    // ⚠️ RESET ALL FILTERS when new data arrives
    setActiveFilters({});
    setSearchTerm('');
    setGlobalFilter('');
    setRowSelection({});
    
    setAppointments(transformedData);
    setIsLoading(false);
  } else {
    console.warn('⚠️ No appointments received from props');
    setIsLoading(false);
    setAppointments([]);
    setOriginalAppointments([]);
    setFilteredOriginalAppointments([]);
  }
}, [propAppointments]);

  // Get selected appointments from the rowSelection state
  const getSelectedAppointments = () => {
    const selectedRows = Object.keys(rowSelection).filter(key => rowSelection[key]);
    return appointments.filter((_, index) => selectedRows.includes(index.toString()));
  };

  
  // Get selected count
  const getSelectedCount = () => {
    return Object.keys(rowSelection).filter(key => rowSelection[key]).length;
  };

  // Function to clear selection
  const clearSelection = () => {
    setRowSelection({});
  };

  // Function to filter appointments
  const handleFilterChange = (filters) => {
    // Store active filters for highlighting
    setActiveFilters(filters);
    
    // Get base data to filter
    const baseData = propAppointments.length > 0 ? 
      transformApiData(propAppointments) : [];
    
    const filtered = baseData.filter(appointment => {
      return (
        (!filters.appointmentId || (appointment.appointmentId && appointment.appointmentId.toString().toLowerCase().includes(filters.appointmentId.toLowerCase()))) &&
        (!filters.patientName || (appointment.patientName && appointment.patientName.toString().toLowerCase().includes(filters.patientName.toLowerCase()))) &&
        (!filters.dateOfBirth || (appointment.dateOfBirth && appointment.dateOfBirth.includes(filters.dateOfBirth))) &&
        (!filters.address || (appointment.address && appointment.address.toString().toLowerCase().includes(filters.address.toLowerCase()))) &&
        (!filters.status || (appointment.status && appointment.status.toString().toLowerCase().includes(filters.status.toLowerCase()))) &&
        (!filters.medicalHistory || (appointment.medicalHistory && appointment.medicalHistory.toString().toLowerCase().includes(filters.medicalHistory.toLowerCase())))
      );
    });

    setAppointments(filtered);
    
    // Update filtered original appointments
    const filteredIds = new Set(filtered.map(item => item.id));
    const filteredOriginal = originalAppointments.filter(appointment => 
      filteredIds.has(appointment._id)
    );
    setFilteredOriginalAppointments(filteredOriginal);
    
    // Reset selection when filters change
    setRowSelection({});
  };

  // Universal search - Fixed to handle MaterialReactTable properly
  const handleUniversalSearch = (term) => {
    setSearchTerm(term);
    
    if (term) {
      const termLower = term.toLowerCase();
      const baseData = propAppointments.length > 0 ? 
        transformApiData(propAppointments) : [];
      
      const searchFiltered = baseData.filter(appointment => {
        return Object.entries(appointment).some(([key, value]) => {
          return typeof value === 'string' && value.toLowerCase().includes(termLower);
        });
      });
      
      setAppointments(searchFiltered);
      
      const filteredIds = new Set(searchFiltered.map(item => item.id));
      const filteredOriginal = originalAppointments.filter(appointment => 
        filteredIds.has(appointment._id)
      );
      setFilteredOriginalAppointments(filteredOriginal);
    } else {
      const transformedData = transformApiData(propAppointments);
      setAppointments(transformedData);
      setFilteredOriginalAppointments(originalAppointments);
    }
    
    setRowSelection({});
  };

  // Handle global filter change from MaterialReactTable
  const handleGlobalFilterChange = (filter) => {
    setGlobalFilter(filter);
    handleUniversalSearch(filter);
  };
  

//   // Función actualizada para manejar cambio de status
// const handleStatusChange = async (id, newStatus) => {
//   try {
//     // Usar el nuevo método del servicio
//     const updatedAppointment = await AppointmentService.updateAppointmentStatus(id, newStatus);
    
//     if (onStatusChange) {
//       onStatusChange(id, newStatus);
//     }
    
//     console.log(`✅ Appointment ${id} status updated to ${newStatus}`);
    
//     // Update local state - appointments (formato tabla)
//     setAppointments(prevAppointments => 
//       prevAppointments.map(appointment => 
//         appointment.id === id ? { ...appointment, status: newStatus } : appointment
//       )
//     );
    
//     // Update originalAppointments (formato API)
//     setOriginalAppointments(prev => 
//       prev.map(apt => 
//         apt._id === id ? {
//           ...apt,
//           status: newStatus,
//           updatedAt: new Date().toISOString()
//         } : apt
//       )
//     );
    
    // Update filteredOriginalAppointments
//     setFilteredOriginalAppointments(prev => 
//       prev.map(apt => 
//         apt._id === id ? {
//           ...apt,
//           status: newStatus,
//           updatedAt: new Date().toISOString()
//         } : apt
//       )
//     );
    
//   } catch (error) {
//     console.error(`❌ Error updating appointment status:`, error);
//     // Opcional: mostrar notificación de error al usuario
//     throw error;
//   }
// };

  // Function to update data after deletion
  const refreshData = (removedItem = null) => {
    if (removedItem) {
      const updatedAppointments = appointments.filter(a => a.id !== removedItem.id);
      setAppointments(updatedAppointments);
      
      const updatedOriginal = originalAppointments.filter(a => a._id !== removedItem.id);
      setOriginalAppointments(updatedOriginal);
      
      const updatedFilteredOriginal = filteredOriginalAppointments.filter(a => a._id !== removedItem.id);
      setFilteredOriginalAppointments(updatedFilteredOriginal);
      
      if (onDeleteAppointments) {
        onDeleteAppointments(removedItem);
      }
      
      setRowSelection({});
    }
  };

  // Function to highlight text based on search terms
  const highlightText = (text, filterTerm, fieldName) => {
    if (text === undefined || text === null) {
      return '';
    }
    
    const textStr = typeof text === 'string' ? text : String(text);
    
    let termToHighlight = '';
    if (fieldName && activeFilters && activeFilters[fieldName]) {
      termToHighlight = activeFilters[fieldName].toLowerCase();
    } else if (searchTerm) {
      termToHighlight = searchTerm.toLowerCase();
    }
    
    const hasGlobalFilter = globalFilter !== undefined && globalFilter !== null && globalFilter !== '';
    if (!termToHighlight && !hasGlobalFilter) {
      return textStr;
    }
    
    let term = termToHighlight;
    if (!term && hasGlobalFilter) {
      term = globalFilter.toLowerCase();
    }
    
    if (!term || !textStr || !textStr.toLowerCase().includes(term)) {
      return textStr;
    }
    
    try {
      const parts = textStr.split(new RegExp(`(${term})`, 'gi'));
      
      return (
        <>
          {parts.map((part, index) => {
            if (!part) return null;
            return part.toLowerCase() === term.toLowerCase() ? 
              <span key={index} style={{ backgroundColor: '#FFFF00', fontWeight: 'bold' }}>{part}</span> : 
              part;
          })}
        </>
      );
    } catch (error) {
      console.error("Error highlighting text:", error);
      return textStr;
    }
  };

  // Function to determine if a field is editable
  const isEditableField = (columnId) => {
    const editableFields = [
      'notes',
      'appointmentType',
      'medicalHistory',
      'address',
      'patientName'
    ];
    return editableFields.includes(columnId);
  };

  // Function to handle cell click for editing
  const handleCellClick = (rowId, columnId, currentValue) => {
    if (isEditableField(columnId)) {
      setEditingCell({ rowId, columnId, value: currentValue });
      setEditValue(currentValue || '');
    }
  };

  // Función actualizada para guardar edición de celda
const handleSaveEdit = async () => {
  if (!editingCell) return;
  
  try {
    const appointment = appointments.find(a => a.id === editingCell.rowId);
    if (!appointment) {
      console.error('❌ Appointment not found for editing');
      return;
    }
    
    // Usar el nuevo método del servicio
    const updatedAppointment = await appointmentService.updateAppointment(
      editingCell.rowId, 
      { [editingCell.columnId]: editValue }
    );
    
    console.log('✅ Appointment field updated:', {
      id: editingCell.rowId,
      field: editingCell.columnId,
      newValue: editValue
    });
    
    // Update local state
    setAppointments(prevAppointments => 
      prevAppointments.map(appointment => 
        appointment.id === editingCell.rowId 
          ? { ...appointment, [editingCell.columnId]: editValue } 
          : appointment
      )
    );
    
    // Update original appointments
    setOriginalAppointments(prev => 
      prev.map(apt => 
        apt._id === editingCell.rowId ? {
          ...apt,
          [editingCell.columnId]: editValue,
          updatedAt: new Date().toISOString()
        } : apt
      )
    );
    
    // Update filtered appointments
    setFilteredOriginalAppointments(prev => 
      prev.map(apt => 
        apt._id === editingCell.rowId ? {
          ...apt,
          [editingCell.columnId]: editValue,
          updatedAt: new Date().toISOString()
        } : apt
      )
    );
    
    setEditingCell(null);
    setEditValue('');
    
  } catch (error) {
    console.error('❌ Error updating appointment field:', error);
    // Opcional: mostrar notificación de error al usuario
  }
};

  // Function to cancel cell edit
  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  
  

  
    

  // Function to get status color and icon
  const getStatusIndicator = (status) => {
  switch (status?.toLowerCase()) {
    case 'pending':
      return { color: '#36373aff', dot: '●' }; // Gray
    case 'waiting':
      return { color: '#F59E0B', dot: '●' }; // Amber/Yellow
    case 'called':
      return { color: '#F97316', dot: '●' }; // Orange
    case 'in consultation':
      return { color: '#0c9869ff', dot: '●' }; // Emerald/Green
    case 'completed':
      return { color: '#3B82F6', dot: '●' }; // Blue
    case 'cancelled':
      return { color: '#EF4444', dot: '●' }; // Red
    case 'no show':
      return { color: '#DC2626', dot: '●' }; // Dark Red
    case 'confirmed':
      return { color: '#bf19aeff', dot: '●' }; // Dark Red
    default:
      return { color: '#9CA3AF', dot: '●' }; // Light Gray
  }
};

  // Memoized column definitions - Updated with English headers and reduced column spacing
  const columns = useMemo(() => [
    {
        id: 'actions',
        header: 'Actions',
        size: 0,
        enableColumnOrdering: false,
        Cell: ({ row }) => (
          <ActionTableButton 
            item={row.original}
            refreshData={() => refreshData(row.original)}
          />          
        ),
      },
    // Checkbox column is handled by MaterialReactTable's enableRowSelection
    // {
    //   accessorKey: 'appointmentId',
    //   header: 'ID',
    //   size: 80,
    //   Cell: ({ cell }) => (
    //     <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>
    //       {highlightText(cell.getValue(), null, 'appointmentId')}
    //     </Typography>
    //   ),
    // },
    {
  accessorKey: 'actions',
  header: 'Actions',
  size: 160,
  enableColumnFilter: false,
  enableSorting: false,
  Cell: ({ row }) => {
    const appointment = row.original;
    const status = appointment.status;
    
    return (
      <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center' }}>
        {/* Call Button - ACTUALIZADO */}
        {!['Completed', 'Cancelled', 'No Show'].includes(status) && (
          <CallButton
            appointment={appointment}
            onCallPatient={(updatedAppointment) => {
              console.log('📞 Patient called:', updatedAppointment.patientName);
              if (onCallAppointment) {
                onCallAppointment(updatedAppointment);
              }
            }}
            onStatusChange={(id, newStatus) => {
              // 🔍 DEBUG: Verificar cambio de estado
              console.log('🔍 Debug CallButton - Status change:', {
                id: id,
                newStatus: newStatus,
                isValidObjectId: /^[0-9a-fA-F]{24}$/.test(id)
              });
              
              // Actualizar estados locales inmediatamente
              setAppointments(prev => 
                prev.map(apt => 
                  apt.id === id ? { ...apt, status: newStatus } : apt
                )
              );
              
              setOriginalAppointments(prev => 
                prev.map(apt => 
                  apt._id === id ? { 
                    ...apt, 
                    status: newStatus, 
                    updatedAt: new Date().toISOString() 
                  } : apt
                )
              );
              
              setFilteredOriginalAppointments(prev => 
                prev.map(apt => 
                  apt._id === id ? { 
                    ...apt, 
                    status: newStatus, 
                    updatedAt: new Date().toISOString() 
                  } : apt
                )
              );
              
              // Ejecutar callback del padre
              if (onStatusChange) {
                onStatusChange(id, newStatus);
              }
            }}
            onReloadAppointments={() => {
              // 🔄 Recargar datos de appointments
              console.log('🔄 Reloading appointments after call...');
              if (onReloadAppointments) {
                onReloadAppointments();
              }
            }}
            size="small"
            title="Call Patient"
          />
        )}
        
        {/* Play Button */}
        <PlayButtonComponent
          appointment={appointment}
          onStatusChange={(updatedAppointment) => {
            console.log('🎯 Cita actualizada desde PlayButton:', updatedAppointment);
            
            // Actualizar estados locales
            setAppointments(prev => 
              prev.map(apt => 
                apt.id === updatedAppointment.id ? updatedAppointment : apt
              )
            );
            
            setOriginalAppointments(prev => 
              prev.map(apt => 
                apt._id === updatedAppointment.id ? {
                  ...apt,
                  status: updatedAppointment.status,
                  notes: updatedAppointment.notes,
                  updatedAt: new Date().toISOString()
                } : apt
              )
            );
            
            setFilteredOriginalAppointments(prev => 
              prev.map(apt => 
                apt._id === updatedAppointment.id ? {
                  ...apt,
                  status: updatedAppointment.status,
                  notes: updatedAppointment.notes,
                  updatedAt: new Date().toISOString()
                } : apt
              )
            );
            
            if (onStatusChange) {
              onStatusChange(updatedAppointment.id, updatedAppointment.status);
            }
          }}
          disabled={false}
        />
        
        {/* Reschedule Button */}
        <RescheduleButton
          appointment={row.original}
          onRescheduleAppointment={(appointment, rescheduleData) => {
            console.log('Appointment rescheduled:', appointment.patientName, rescheduleData);
            
            if (onRescheduleAppointment) {
              onRescheduleAppointment(appointment);
            }
            
            // Recargar datos después de reagendar
            if (onReloadAppointments) {
              setTimeout(() => {
                onReloadAppointments();
              }, 1000);
            }
          }}
          size="small"
          title="Reschedule Appointment"
        />
        
        {/* Walk-in Button */}
        <WalkinButtonComponent 
          appointment={appointment}
          onStatusChange={(id, newStatus) => {
            console.log('🔍 Debug WalkIn - ID recibido:', {
              id: id,
              newStatus: newStatus,
              isValidObjectId: /^[0-9a-fA-F]{24}$/.test(id)
            });
            
            // Actualiza localmente los estados tras cambio
            setAppointments(prev =>
              prev.map(apt => apt.id === id ? { ...apt, status: newStatus } : apt)
            );

            setOriginalAppointments(prev =>
              prev.map(apt => apt._id === id ? { 
                ...apt, 
                status: newStatus, 
                updatedAt: new Date().toISOString() 
              } : apt)
            );

            setFilteredOriginalAppointments(prev =>
              prev.map(apt => apt._id === id ? { 
                ...apt, 
                status: newStatus, 
                updatedAt: new Date().toISOString() 
              } : apt)
            );

            // Propaga a padre si aplica
            if (onStatusChange) {
              onStatusChange(id, newStatus);
            }
          }}
        />
        {/* No Show Button */}
        <NoShowComponent
  appointment={appointment}
  onStatusChange={(appointmentId, newStatus) => {
    console.log('🚫 Cita marcada como No Show:', { appointmentId, newStatus });
    
    // Crear el objeto updatedAppointment para mantener consistencia
    const updatedAppointment = {
      ...appointment,
      id: appointmentId,
      status: newStatus,
      notes: 'Patient did not show up for the appointment',
      updatedAt: new Date().toISOString()
    };
    
    // Actualizar estados locales (mismo patrón que PlayButton)
    setAppointments(prev => 
      prev.map(apt => 
        apt.id === updatedAppointment.id ? updatedAppointment : apt
      )
    );
    
    setOriginalAppointments(prev => 
      prev.map(apt => 
        apt._id === updatedAppointment.id ? {
          ...apt,
          status: updatedAppointment.status,
          notes: updatedAppointment.notes,
          updatedAt: new Date().toISOString()
        } : apt
      )
    );
    
    setFilteredOriginalAppointments(prev => 
      prev.map(apt => 
        apt._id === updatedAppointment.id ? {
          ...apt,
          status: updatedAppointment.status,
          notes: updatedAppointment.notes,
          updatedAt: new Date().toISOString()
        } : apt
      )
    );
    
    if (onStatusChange) {
      onStatusChange(updatedAppointment.id, updatedAppointment.status);
    }
  }}
  disabled={false}
/>
      </Box>
    );
  },
},

    {
      accessorKey: 'patientName',
      header: 'Patient',
      size: 200,
      Cell: ({ cell, row }) => {
        const isEditing = editingCell?.rowId === row.original.id && editingCell?.columnId === 'patientName';
        
        if (isEditing) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TextField
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                size="small"
                autoFocus
                sx={{ flexGrow: 1 }}
              />
              <IconButton size="small" onClick={handleSaveEdit}>
                <SaveIcon />
              </IconButton>
              <IconButton size="small" onClick={handleCancelEdit}>
                <CancelIcon />
              </IconButton>
            </Box>
          );
        }
        
        return (
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5,
              cursor: 'pointer'
            }}
            onClick={() => handleCellClick(row.original.id, 'patientName', cell.getValue())}
          >
            <Avatar
              src={row.original.patientAvatar}
              sx={{ width: 28, height: 28 }}
            >
              <PersonIcon sx={{ fontSize: 16 }} />
            </Avatar>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>
                {highlightText(cell.getValue(), null, 'patientName')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                {row.original.dateOfBirth ? new Date(row.original.dateOfBirth).toLocaleDateString('en-US', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                }) : 'No date'}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      accessorKey: 'date',
      header: 'Appointment Date',
      size: 120,
      Cell: ({ cell, row }) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>
            {cell.getValue() ? new Date(cell.getValue()).toLocaleDateString('es-ES', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            }) : 'No date'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {row.original.startTime && row.original.endTime ? 
              `${row.original.startTime} - ${row.original.endTime}` : 
              'No schedule'
            }
          </Typography>
        </Box>
      ),
    },
    {
      accessorKey: 'doctorName',
      header: 'Doctor',
      size: 160,
      Cell: ({ cell, row }) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>
            {highlightText(cell.getValue() || 'Unassigned', null, 'doctorName')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {row.original.doctorRole || 'No specialty'}
          </Typography>
        </Box>
      ),
    },
    // Agregar esta columna después de la columna de 'doctorName' (alrededor de la línea 450)
{
  accessorKey: 'despachoName',
  header: 'Office',
  size: 160,
  Cell: ({ cell, row }) => (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>
        {highlightText(cell.getValue() || 'Not assigned', null, 'despachoName')}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
        {row.original.despachoCode || 'No code'}
      </Typography>
    </Box>
  ),
},
    {
      accessorKey: 'status',
      header: 'Status',
      size: 120,
      Cell: ({ cell, row }) => {
        const status = cell.getValue();
        const statusIndicator = getStatusIndicator(status);
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: statusIndicator.color, 
                fontSize: '16px',
                lineHeight: 1
              }}
            >
              {statusIndicator.dot}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.875rem',  color: statusIndicator.color }}>
              {highlightText(status, null, 'status')}
            </Typography>
          </Box>
        );
      },
    },
    {
      accessorKey: 'address',
      header: 'Address',
      size: 140,
      Cell: ({ cell, row }) => {
        const isEditing = editingCell?.rowId === row.original.id && editingCell?.columnId === 'address';
        
        if (isEditing) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TextField
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                size="small"
                autoFocus
                sx={{ flexGrow: 1 }}
              />
              <IconButton size="small" onClick={handleSaveEdit}>
                <SaveIcon />
              </IconButton>
              <IconButton size="small" onClick={handleCancelEdit}>
                <CancelIcon />
              </IconButton>
            </Box>
          );
        }
        
        return (
          <Typography 
            variant="body2" 
            sx={{ cursor: 'pointer', fontSize: '0.875rem' }}
            onClick={() => handleCellClick(row.original.id, 'address', cell.getValue())}
          >
            {highlightText(cell.getValue(), null, 'address')}
          </Typography>
        );
      },
    },
    {
      accessorKey: 'medicalHistory',
      header: 'Medical History',
      size: 150,
      Cell: ({ cell, row }) => {
        const isEditing = editingCell?.rowId === row.original.id && editingCell?.columnId === 'medicalHistory';
        
        if (isEditing) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TextField
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                size="small"
                autoFocus
                multiline
                rows={2}
                sx={{ flexGrow: 1 }}
              />
              <IconButton size="small" onClick={handleSaveEdit}>
                <SaveIcon />
              </IconButton>
              <IconButton size="small" onClick={handleCancelEdit}>
                <CancelIcon />
              </IconButton>
            </Box>
          );
        }
        
        return (
          <Typography 
            variant="body2"
            sx={{ cursor: 'pointer', fontSize: '0.875rem' }}
            onClick={() => handleCellClick(row.original.id, 'medicalHistory', cell.getValue())}
          >
            {highlightText(cell.getValue(), null, 'medicalHistory')}
          </Typography>
        );
      },
    },    
{
      accessorKey: 'appointmentType',
      header: 'Type',
      size: 120,
      Cell: ({ cell, row }) => {
        const isEditing = editingCell?.rowId === row.original.id && editingCell?.columnId === 'appointmentType';
        
        if (isEditing) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TextField
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                size="small"
                autoFocus
                sx={{ flexGrow: 1 }}
              />
              <IconButton size="small" onClick={handleSaveEdit}>
                <SaveIcon />
              </IconButton>
              <IconButton size="small" onClick={handleCancelEdit}>
                <CancelIcon />
              </IconButton>
            </Box>
          );
        }
        
        return (
          <Typography 
            variant="body2"
            sx={{ cursor: 'pointer', fontSize: '0.875rem' }}
            onClick={() => handleCellClick(row.original.id, 'appointmentType', cell.getValue())}
          >
            {highlightText(cell.getValue() || 'General', null, 'appointmentType')}
          </Typography>
        );
      },
    },
    {
      accessorKey: 'checkInTime',
      header: 'Check-In',
      size: 100,
      Cell: ({ cell }) => {
        const checkInTime = cell.getValue();
        if (!checkInTime) return <Typography variant="caption" color="text.secondary">-</Typography>;
        
        return (
          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
            {new Date(checkInTime).toLocaleTimeString('es-ES', {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </Typography>
        );
      },
    },
    {
      accessorKey: 'waitTime',
      header: 'Wait Time',
      size: 90,
      Cell: ({ cell }) => {
        const waitTime = cell.getValue();
        if (!waitTime || waitTime === 0) {
          return <Typography variant="caption" color="text.secondary">-</Typography>;
        }
        
        return (
          <Typography variant="body2" sx={{ fontSize: '0.875rem', color: waitTime > 30 ? '#EF4444' : '#0c9869ff' }}>
            {waitTime} min
          </Typography>
        );
      },
    },
    {
      accessorKey: 'consultationDuration',
      header: 'Duration',
      size: 90,
      Cell: ({ cell }) => {
        const duration = cell.getValue();
        if (!duration || duration === 0) {
          return <Typography variant="caption" color="text.secondary">-</Typography>;
        }
        
        return (
          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
            {duration} min
          </Typography>
        );
      },
    },
    {
      accessorKey: 'medicalHistory',
      header: 'Medical History',
      size: 150,
      Cell: ({ cell, row }) => {
        const isEditing = editingCell?.rowId === row.original.id && editingCell?.columnId === 'medicalHistory';
        
        if (isEditing) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TextField
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                size="small"
                autoFocus
                multiline
                rows={2}
                sx={{ flexGrow: 1 }}
              />
              <IconButton size="small" onClick={handleSaveEdit}>
                <SaveIcon />
              </IconButton>
              <IconButton size="small" onClick={handleCancelEdit}>
                <CancelIcon />
              </IconButton>
            </Box>
          );
        }
        
        return (
          <Tooltip title={cell.getValue() || ''}>
            <Typography 
              variant="body2"
              sx={{ 
                cursor: 'pointer', 
                fontSize: '0.875rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              onClick={() => handleCellClick(row.original.id, 'medicalHistory', cell.getValue())}
            >
              {highlightText(cell.getValue() || 'No history', null, 'medicalHistory')}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'notes',
      header: 'Notes',
      size: 150,
      Cell: ({ cell, row }) => {
        const isEditing = editingCell?.rowId === row.original.id && editingCell?.columnId === 'notes';
        
        if (isEditing) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TextField
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                size="small"
                autoFocus
                multiline
                rows={2}
                sx={{ flexGrow: 1 }}
              />
              <IconButton size="small" onClick={handleSaveEdit}>
                <SaveIcon />
              </IconButton>
              <IconButton size="small" onClick={handleCancelEdit}>
                <CancelIcon />
              </IconButton>
            </Box>
          );
        }
        
        return (
          <Tooltip title={cell.getValue() || ''}>
            <Typography 
              variant="body2"
              sx={{ 
                cursor: 'pointer', 
                fontSize: '0.875rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              onClick={() => handleCellClick(row.original.id, 'notes', cell.getValue())}
            >
              {highlightText(cell.getValue() || 'No notes', null, 'notes')}
            </Typography>
          </Tooltip>
        );
      },
    },
  ], [editingCell, editValue, activeFilters, searchTerm, globalFilter]);

  // Memoized filtered data
  const filteredData = useMemo(() => {
    return appointments;
  }, [appointments]);

  return (
    <div className="container-fluid px-lg-5">
          {/* Breadcrumb */}
                            <nav className="breadcrumb-container d-none d-md-block pt-3 mb-2" aria-label="breadcrumb">
                                <ol className="breadcrumb fs-sm mb-0">
                                    <li className="breadcrumb-item"><Link to="#">Dashboard</Link></li>
                                    <li className="breadcrumb-item active" aria-current="page">Appointments Timeline</li>
                                </ol>
                            </nav>
    {/* KPI Component */}
      <AppointmentTimelineKPI
        appointments={filteredOriginalAppointments}
        selectedAppointments={getSelectedAppointments()}
        currentView={currentTimelineView}
        dateRange={selectedDateRange}
      />
      {/* Search and Filter Bar */}
      <AppointmentFiltersHeader 
        onFilterChange={handleFilterChange} 
        onSearchChange={handleUniversalSearch}
        selectedAppointments={getSelectedAppointments()}
        onClearSelection={() => setRowSelection({})}
        onBulkDelete={(appointmentsToDelete) => {
          // Remove selected appointments from local state
          const remainingAppointments = appointments.filter(
            appointment => !appointmentsToDelete.some(a => a.id === appointment.id)
          );
          
          setAppointments(remainingAppointments);
          
          // Also update original appointments
          const deletedIds = new Set(appointmentsToDelete.map(a => a.id));
          const updatedOriginal = originalAppointments.filter(a => !deletedIds.has(a._id));
          setOriginalAppointments(updatedOriginal);
          
          // Update filtered appointments
          const updatedFilteredOriginal = filteredOriginalAppointments.filter(a => !deletedIds.has(a._id));
          setFilteredOriginalAppointments(updatedFilteredOriginal);
          
          // Notify parent component about bulk deletions - pass the full array
          if (onDeleteAppointments && appointmentsToDelete.length > 0) {
            onDeleteAppointments(appointmentsToDelete); // Pass the complete array
          }
          
          setRowSelection({});
        }}
        onReloadData={() => {
          // Reload all appointments data
          // This function should reset filters and reload the complete dataset
          if (onReloadAppointments) {
            onReloadAppointments();
          }
        }}
      />
      
      {/* Main Table */}
      <MaterialReactTable
        columns={columns}
        data={filteredData}
        enableRowSelection
        enableMultiRowSelection
        enableSelectAll
        enableGlobalFilter
        globalFilterValue={globalFilter}
        onGlobalFilterChange={handleGlobalFilterChange}
        enableColumnFilters
        enableSorting
        enablePagination
        enableBottomToolbar
        enableTopToolbar
        muiTableContainerProps={{
          sx: { maxHeight: '100%' }
        }}
        muiTableProps={{
          sx: {
            tableLayout: 'fixed',
          }
        }}
        // Reduced header cell spacing and padding
        muiTableHeadCellProps={{
          sx: {
            backgroundColor: 'white',
            fontWeight: 'bold',
            padding: '8px 4px',
            fontSize: '0.875rem'
          }
        }}
        // Reduced body cell spacing and padding
        muiTableBodyCellProps={{
          sx: {
            padding: '6px 4px',
            fontSize: '0.875rem'
          }
        }}
        state={{
          rowSelection,
          isLoading,
          globalFilter,
        }}
        onRowSelectionChange={setRowSelection}
        initialState={{
          pagination: {
            pageSize: 25,
            pageIndex: 0,
          },
          density: 'compact',
        }}
      />    
      
    </div>
  );
};

export default AppointmentTimelineTable;